// src/services/accountStockService.js
// Admin-only: manage Steam account inventory
// ─────────────────────────────────────────────────────────────────
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  writeBatch,
  increment,
} from "firebase/firestore";
import { db } from "./firebase";

/**
 * Get all accounts for a specific game (admin only).
 * NOTE: Firestore rules block client reads of /accounts.
 * This only works when called with Admin SDK (Cloud Functions)
 * or via a special admin endpoint. For the admin panel UI,
 * use a Cloud Function wrapper instead.
 */
export async function getAccountsForGame(gameId) {
  const q = query(
    collection(db, "accounts"),
    where("gameId", "==", gameId)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    id: d.id,
    gameId: d.data().gameId,
    status: d.data().status,
    soldAt: d.data().soldAt,
    // NOTE: steamEmail and steamPassword are intentionally omitted from UI
  }));
}

/**
 * Add a single Steam account to stock (admin only).
 */
export async function addAccount(gameId, steamEmail, steamPassword, note = "") {
  const docRef = await addDoc(collection(db, "accounts"), {
    gameId,
    steamEmail,
    steamPassword,
    note,
    status: "available",
    soldTo: null,
    soldAt: null,
    orderId: null,
    addedAt: serverTimestamp(),
  });

  // Increment game stock count
  await updateDoc(doc(db, "games", gameId), {
    stock: increment(1),
  });

  return docRef.id;
}

/**
 * Bulk add accounts from CSV data (admin only).
 * csvRows format: [{ steamEmail, steamPassword, note }]
 */
export async function bulkAddAccounts(gameId, csvRows) {
  const batch = writeBatch(db);
  const accountsRef = collection(db, "accounts");

  csvRows.forEach((row) => {
    const newRef = doc(accountsRef);
    batch.set(newRef, {
      gameId,
      steamEmail: row.steamEmail.trim(),
      steamPassword: row.steamPassword.trim(),
      note: row.note || "",
      status: "available",
      soldTo: null,
      soldAt: null,
      orderId: null,
      addedAt: serverTimestamp(),
    });
  });

  await batch.commit();

  // Update stock count
  await updateDoc(doc(db, "games", gameId), {
    stock: increment(csvRows.length),
  });

  return csvRows.length;
}

/**
 * Delete an available account (admin only).
 * Cannot delete sold accounts.
 */
export async function deleteAccount(accountId, gameId) {
  const accountRef = doc(db, "accounts", accountId);
  await deleteDoc(accountRef);

  // Decrement game stock
  await updateDoc(doc(db, "games", gameId), {
    stock: increment(-1),
  });
}
